import type { ReactNode } from 'react';

import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { formatDateTime, formatRelative } from '@/utils/format';

/**
 * Chronological entries: activity on a record, follow-up history, a change log.
 *
 * Times are relative while they are recent and absolute once they are not, because
 * "3 hours ago" is what a pastor wants for this morning's follow-up and useless for
 * something from March.
 */

export interface TimelineEntry {
  id: string;
  /** UTC ISO 8601. */
  at: string;
  title: string;
  description?: string;
  /** Who did it, already formatted. */
  actor?: string;
  icon?: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  /** Extra content — a diff, a message body, an attachment row. */
  content?: ReactNode;
}

const TONES = {
  neutral: 'bg-surface-sunken text-fg-tertiary',
  success: 'bg-success-subtle text-success-fg',
  warning: 'bg-warning-subtle text-warning-fg',
  danger: 'bg-danger-subtle text-danger-fg',
  info: 'bg-info-subtle text-info-fg',
} as const;

export const Timeline = ({
  entries,
  className,
}: {
  entries: TimelineEntry[];
  className?: string;
}) => {
  const { locale, timezone } = usePreferences();

  return (
    <ol className={cn('relative space-y-5', className)}>
      {entries.map((entry, index) => (
        <li key={entry.id} className="relative flex gap-3">
          {/* The rail stops at the last entry rather than trailing into nothing. */}
          {index < entries.length - 1 ? (
            <span
              aria-hidden
              className="absolute top-8 left-[0.9375rem] h-[calc(100%+0.5rem)] w-px bg-border"
            />
          ) : null}

          <span
            className={cn(
              'z-10 flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full',
              entry.icon ? 'bg-transparent' : TONES[entry.tone ?? 'neutral'],
            )}
          >
            {entry.icon}
          </span>

          <div className="min-w-0 flex-1 pt-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <p className="text-body font-medium text-fg">{entry.title}</p>
              <time
                dateTime={entry.at}
                title={formatDateTime(entry.at, { locale, timezone, withZone: true })}
                className="text-caption text-fg-tertiary"
              >
                {formatRelative(entry.at, { locale, timezone })}
              </time>
            </div>

            {entry.actor ? (
              <p className="mt-0.5 text-body-sm text-fg">{entry.actor}</p>
            ) : null}
            {entry.description ? (
              <p className="mt-0.5 text-body-sm text-fg-secondary">{entry.description}</p>
            ) : null}
            {entry.content ? <div className="mt-2">{entry.content}</div> : null}
          </div>
        </li>
      ))}
    </ol>
  );
};
